import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {fieldRules} from 'utils/helper';
import {STEP_TEMPLATES} from './constants';
import {ContextStep} from './ContextStep';
import {FaqPicker} from './FaqPicker';
import styles from './FlowBuilder.module.scss';

const SectionLabel = ({title, hint}) => (
  <div className={styles.sectionLabel}>
    <span>{title}</span>
    {hint ? <small>{hint}</small> : null}
  </div>
);

export const IssueForm = ({
  form,
  mode,
  language,
  skillOptions,
  skillsLoading,
  resolverCatalog,
  resolverCatalogLoading,
  initialFaqs,
  parentCategory,
  parentFeature,
  onFinish,
  submitting,
}) => {
  const stepTemplate = Form.useWatch('stepTemplate', form);
  const fallthrough = Form.useWatch('fallthrough', form);
  const isEdit = mode === 'edit';
  // Context resolvers take a userId; anonymous tickets have none, so a
  // CONTEXT_SELECT issue exposed anonymously would dead-end. Keep them mutually
  // exclusive — and force the flag off the moment the template becomes context.
  const isContextSelect = stepTemplate === 'CONTEXT_SELECT';

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={changed => {
        if (changed.stepTemplate === 'CONTEXT_SELECT') {
          form.setFieldValue('availableToAnonymous', false);
        }
      }}
      className={styles.issueFormRoot}>
      <div className={styles.issueFormHeader}>
        <div>
          <h2>{isEdit ? 'Update Issue' : 'Create Issue'}</h2>
          {parentCategory || parentFeature ? (
            <p className={styles.formContext}>
              {isEdit ? 'in' : 'under'}{' '}
              <strong>
                {[parentCategory?.name, parentFeature?.name]
                  .filter(Boolean)
                  .join(' / ')}
              </strong>
            </p>
          ) : null}
        </div>
        <Tag color="blue">{language}</Tag>
      </div>

      <div className={styles.issueFormBody}>
        <SectionLabel title="Basics" />
        <Form.Item name="title" label="Title" rules={[fieldRules('Title')]}>
          <Input placeholder="e.g. My deposit is pending" />
        </Form.Item>

        <div className={styles.fieldGrid}>
          <Form.Item
            name="stepTemplate"
            label="Step template"
            rules={[fieldRules('Step template')]}>
            <Select options={STEP_TEMPLATES} placeholder="Select template" />
          </Form.Item>
          <Form.Item
            name="sortOrder"
            label="Sort order"
            rules={[{type: 'number', min: 1, message: 'Must be at least 1'}]}>
            <InputNumber min={1} style={{width: '100%'}} />
          </Form.Item>
        </div>

        <Divider className={styles.sectionDivider} />
        <SectionLabel
          title="AI routing"
          hint={
            fallthrough
              ? 'This issue escalates straight to a human — the skill below is never used. FAQs still show before the hand-off.'
              : 'Linked skill + the FAQ articles the AI may quote.'
          }
        />

        <Form.Item
          name="skillId"
          label="Skill"
          tooltip="Linked AI skill. Leave empty to fall back to the domain registry skill.">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={skillsLoading ? 'Loading skills…' : 'Select skill…'}
            loading={skillsLoading}
            options={skillOptions}
            notFoundContent={skillsLoading ? 'Loading…' : undefined}
          />
        </Form.Item>

        <Form.Item
          name="faqPostIds"
          label="FAQs"
          tooltip="Search by question text and select. Required for SIMPLE; optional for CONTEXT_SELECT; must be empty for DIRECT.">
          <FaqPicker language={language} initialFaqs={initialFaqs} />
        </Form.Item>

        {stepTemplate === 'CONTEXT_SELECT' && (
          <>
            <Divider className={styles.sectionDivider} />
            <SectionLabel
              title="Context fetch steps"
              hint="Server resolves these in order before the AI runs."
            />
            <Form.List name="contextConfig">
              {(fields, {add, remove}) => (
                <div className={styles.contextSection}>
                  {fields.map((field, idx) => (
                    <ContextStep
                      key={field.key}
                      field={field}
                      index={idx}
                      form={form}
                      catalog={resolverCatalog}
                      catalogLoading={resolverCatalogLoading}
                      onRemove={() => remove(field.name)}
                    />
                  ))}
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => add({})}>
                    Add context step
                  </Button>
                </div>
              )}
            </Form.List>
          </>
        )}

        <Divider className={styles.sectionDivider} />

        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="availableToAnonymous"
          label="Available to anonymous"
          valuePropName="checked"
          tooltip="Expose to logged-out users. Requires the parent category and feature to be flagged too. Not allowed for CONTEXT_SELECT — context resolvers need a signed-in userId."
          extra={
            isContextSelect
              ? 'Unavailable for CONTEXT_SELECT issues — they require a signed-in user.'
              : undefined
          }>
          <Switch disabled={isContextSelect} />
        </Form.Item>

        <Form.Item
          name="fallthrough"
          label="Skip AI — escalate to a human agent"
          valuePropName="checked"
          tooltip="Works with every step template — FAQ and context steps still run before the hand-off. The server enforces it too: any message on such a ticket escalates rather than invoking the AI."
          extra="Selecting this issue routes the ticket straight to a live agent instead of the AI assistant.">
          <Switch />
        </Form.Item>
      </div>

      <div className={styles.issueFormFooter}>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {mode === 'edit' ? 'Save Changes' : 'Create'}
        </Button>
      </div>
    </Form>
  );
};
